// ======================================================================
// File: src/modules/ppe/pages/procurement/purchaseInvoice/form/PurchaseInvoiceFormDrawer.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - Drawer component for creating Purchase Invoice
// - Vendor Search using SearchableDropdown
// - Select POSTED GRN
// - Auto load GRN lines
// - Calculate GST and Totals
// - Additional Charges UI (Phase 1 Only)
// - Attachment UI (No Upload Integration Yet)
// ======================================================================

import React, { useEffect, useMemo, useState } from "react";

import purchaseInvoiceService from "../services/purchaseInvoiceService";

import grnService from "../../grn/services/grnService";
import vendorLookupService from "../../grn/services/vendorLookupService";

import SearchableDropdown from "../../../../components/common/SearchableDropdown";

export default function PurchaseInvoiceFormDrawer({
  open,
  mode = "create",
  record,
  onClose,
  onSaved,
}) {
  const [saving, setSaving] = useState(false);

  const [attachment, setAttachment] = useState(null);
  const [isMaximized, setIsMaximized] = useState(true);

  const [form, setForm] = useState({
    pi_date: new Date().toISOString().split("T")[0],

    vendor_id: "",
    vendor_label: "",

    invoice_type: "GOODS",

    supplier_invoice_no: "",
    supplier_invoice_date: "",

    due_date: "",

    currency_code: "INR",
    exchange_rate: 1,

    remarks: "",

    selected_grn_id: "",
    selected_grn_label: "",

    freight_charge: 0,
    insurance_charge: 0,
    other_charge: 0,
  });

  const [invoiceLines, setInvoiceLines] = useState([]);

  // ----------------------------------------------------------
  // RESET FORM
  // ----------------------------------------------------------

  useEffect(() => {
    if (!open) return;

    setForm({
      pi_date: new Date().toISOString().split("T")[0],

      vendor_id: "",
      vendor_label: "",

      invoice_type: "GOODS",

      supplier_invoice_no: "",
      supplier_invoice_date: "",

      due_date: "",

      currency_code: "INR",
      exchange_rate: 1,

      remarks: "",

      selected_grn_id: "",
      selected_grn_label: "",

      freight_charge: 0,
      insurance_charge: 0,
      other_charge: 0,
    });

    setInvoiceLines([]);
    setAttachment(null);
  }, [open]);

  // ----------------------------------------------------------
  // NORMAL INPUT CHANGE
  // ----------------------------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ----------------------------------------------------------
  // LOAD POSTED GRN OPTIONS
  // ----------------------------------------------------------

  const fetchPostedGRNs = async (search) => {
    try {
      const response = await grnService.getGRNList();

      const rows = response?.data || [];

      return rows.filter(
        (x) =>
          x.status === "POSTED" &&
          (!form.vendor_id || x.vendor_id === form.vendor_id) &&
          (
            !search ||
            x.grn_no?.toLowerCase().includes(search.toLowerCase()) ||
            x.po_no?.toLowerCase().includes(search.toLowerCase())
          )
      );
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // ----------------------------------------------------------
  // LOAD GRN DETAILS
  // ----------------------------------------------------------

  const loadGRNLines = async (grnId) => {
    try {
      const response = await grnService.getGRNById(grnId);

      const lines = response?.data?.lines || [];

      const mappedLines = lines.map((line) => ({
        grn_header_id: grnId,
        grn_line_id: line.id,

        material_id: line.material_id,
        material_type: line.material_type,
        material_name: line.material_name || "",

        uom: line.uom || "",

        grn_qty:
          Number(
            line.received_qty ||
            line.qty ||
            0
          ),

        invoice_qty:
          Number(
            line.received_qty ||
            line.qty ||
            0
          ),

        unit_price: Number(line.unit_price || 0),

        tax_percent: Number(line.tax_percent || 18),

        tax_value: 0,
        line_total: 0,
      }));

      setInvoiceLines(mappedLines);
    } catch (error) {
      console.error(error);
      alert("Failed to load GRN details");
    }
  };

  // ----------------------------------------------------------
  // LINE CHANGE
  // ----------------------------------------------------------

  const handleLineChange = (index, field, value) => {
    const updated = [...invoiceLines];

    updated[index][field] = value;

    setInvoiceLines(updated);
  };

  // ----------------------------------------------------------
  // TOTALS
  // ----------------------------------------------------------

  const totals = useMemo(() => {
    let materialTotal = 0;
    let gstTotal = 0;

    invoiceLines.forEach((line) => {
      const qty = Number(line.invoice_qty || 0);
      const price = Number(line.unit_price || 0);
      const taxPercent = Number(line.tax_percent || 0);

      const taxable = qty * price;
      const tax = (taxable * taxPercent) / 100;

      materialTotal += taxable;
      gstTotal += tax;
    });

    const additionalCharges =
      Number(form.freight_charge || 0) +
      Number(form.insurance_charge || 0) +
      Number(form.other_charge || 0);

    return {
      materialTotal,
      gstTotal,
      additionalCharges,
      grandTotal:
        materialTotal +
        gstTotal +
        additionalCharges,
    };
  }, [invoiceLines, form]);

  // ----------------------------------------------------------
  // SAVE
  // ----------------------------------------------------------

  const handleSave = async () => {
    try {
      if (!form.vendor_id) {
        alert("Please select Vendor");
        return;
      }

      if (!form.supplier_invoice_no) {
        alert("Please enter Supplier Invoice No");
        return;
      }
      if (!form.supplier_invoice_date) {
        alert("Please select Supplier Invoice Date");
        return;
      }

      if (!form.due_date) {
        alert("Please select Due Date");
        return;
      }
      

      setSaving(true);

      //console.log(
      //  "Selected GRN Lines",
      //  selectedLines
      //);

      const payload = {
        vendor_id: form.vendor_id,
        pi_date: form.pi_date,
        invoice_type: form.invoice_type,
        supplier_invoice_no: form.supplier_invoice_no,
        supplier_invoice_date: form.supplier_invoice_date,
        due_date: form.due_date,
        currency_code: form.currency_code,
        exchange_rate: Number(form.exchange_rate),
        remarks: form.remarks
      };
      const response =
        await purchaseInvoiceService.createPurchaseInvoice(
          payload
        );

      alert("Purchase Invoice created successfully");

      if (onSaved) {
        onSaved(response.data);
      }

      onClose();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Failed to create Purchase Invoice"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="erp-drawer-overlay">
      <div
        className={
          isMaximized
            ? "erp-drawer erp-drawer-maximized"
            : "erp-drawer"
        }
      >
        <div className="erp-drawer-header">

          <h5 className="mb-0">
            Create Purchase Invoice
          </h5>

          <div className="d-flex gap-2">

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setIsMaximized(!isMaximized)
              }
            >
              {isMaximized ? "❐" : "□"}
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={onClose}
            >
              ✕
            </button>

          </div>

        </div>

        <div className="erp-drawer-body">
          <div className="row g-3">

            <div className="col-6">
              <label className="form-label">
                Purchase Invoice Date
              </label>

              <input
                type="date"
                className="form-control"
                name="pi_date"
                value={form.pi_date}
                onChange={handleChange}
              />
            </div>

            <div className="col-6">
              <SearchableDropdown
                label="Vendor"
                value={form.vendor_label}
                placeholder="Search Vendor..."
                fetchOptions={vendorLookupService.search}
                getOptionLabel={(o) =>
                  `${o.bp_code} - ${o.bp_name}`
                }
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    vendor_id: v.id,
                    vendor_label:
                      `${v.bp_code} - ${v.bp_name}`,

                    selected_grn_id: "",
                    selected_grn_label: "",
                  }))
                }
              />
            </div>

            <div className="col-4">
              <label className="form-label">
                Invoice Type
              </label>

              <select
                className="form-select"
                name="invoice_type"
                value={form.invoice_type}
                onChange={handleChange}
              >
                <option value="GOODS">GOODS</option>
                <option value="SERVICE">SERVICE</option>
                <option value="CAPEX">CAPEX</option>
              </select>
            </div>

            <div className="col-4">
              <label className="form-label">
                Supplier Invoice No <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                className="form-control"
                name="supplier_invoice_no"
                value={form.supplier_invoice_no}
                onChange={handleChange}
              />
            </div>

            <div className="col-4">
              <label className="form-label">
                Supplier Invoice Date <span className="text-danger">*</span>
              </label>

              <input
                type="date"
                className="form-control"
                name="supplier_invoice_date"
                value={form.supplier_invoice_date}
                onChange={handleChange}
              />
            </div>

            <div className="col-4">
              <label className="form-label">
                Due Date <span className="text-danger">*</span>
              </label>

              <input
                type="date"
                className="form-control"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
              />
            </div>

            <div className="col-4">
              <label className="form-label">
                Currency
              </label>

              <input
                type="text"
                className="form-control"
                name="currency_code"
                value={form.currency_code}
                onChange={handleChange}
              />
            </div>

            <div className="col-4">
              <label className="form-label">
                Exchange Rate
              </label>

              <input
                type="number"
                className="form-control"
                name="exchange_rate"
                value={form.exchange_rate}
                onChange={handleChange}
              />
            </div>

            

            
            

            
            <div className="col-12">
              <label className="form-label">
                Remarks
              </label>

              <textarea
                className="form-control"
                rows="3"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
              />
            </div>

            
          </div>
        </div>

        <div className="erp-drawer-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}